import { NgModule } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Http, HttpModule } from '@angular/http';
import { HttpClientModule } from "@angular/common/http";
import { BrowserModule } from "@angular/platform-browser";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { TranslateModule, TranslateLoader } from "@ngx-translate/core";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";

import { AppRoutes } from "./app.routes";
import "rxjs/add/operator/toPromise";

import {
  AccordionModule,
  ConfirmationService,
  ProgressSpinnerModule,
  AutoCompleteModule,
  BreadcrumbModule,
  ButtonModule,
  CalendarModule,
  CarouselModule,
  ColorPickerModule,
  ChartModule,
  CheckboxModule,
  ChipsModule,
  CodeHighlighterModule,
  ConfirmDialogModule,
  SharedModule,
  ContextMenuModule,
  DataGridModule,
  DataListModule,
  DataScrollerModule,
  DataTableModule,
  DialogModule,
  DragDropModule,
  DropdownModule,
  EditorModule,
  FieldsetModule,
  FileUploadModule,
  GalleriaModule,
  GMapModule,
  GrowlModule,
  InputMaskModule,
  InputSwitchModule,
  InputTextModule,
  InputTextareaModule,
  LightboxModule,
  ListboxModule,
  MegaMenuModule,
  MenuModule,
  MenubarModule,
  MessageModule,
  MessagesModule,
  MultiSelectModule,
  OrderListModule,
  OrganizationChartModule,
  OverlayPanelModule,
  PaginatorModule,
  PanelModule,
  PanelMenuModule,
  PasswordModule,
  PickListModule,
  ProgressBarModule,
  RadioButtonModule,
  RatingModule,
  ScheduleModule,
  SelectButtonModule,
  SlideMenuModule,
  SliderModule,
  SpinnerModule,
  SplitButtonModule,
  StepsModule,
  TabMenuModule,
  TabViewModule,
  TerminalModule,
  TieredMenuModule,
  ToggleButtonModule,
  ToolbarModule,
  TooltipModule,
  TreeModule,
  TreeTableModule,
  TriStateCheckboxModule,
} from 'primeng/primeng';

import { AppComponent } from "./app.component";
import { AppTopBar } from "./app.topbar.component";
import { AppFooter } from "./app.footer.component";
import { AppRightPanel } from "./app.rightpanel.component";
import { InlineProfileComponent } from "./app.profile.component";
import { AppMenuComponent, AppSubMenu } from "./app.menu.component";

/* Easy Pipes */
import { GetWeekDayPipe } from "./shared/pipe/get-week-day.pipe";
import { GetBooleanPipe } from "./shared/pipe/get-boolean.pipe";
import { ConvertDatePipe } from "./shared/pipe/convert-date.pipe";
import { ConvertSecondsToDatePipe } from "./shared/pipe/convert-seconds-to-date.pipe";
import { ReplacePipe } from "./shared/pipe/replace.pipe";
import { DecimalPipe } from "./shared/pipe/decimal.pipe";
import { ConvertFullDatePipe } from "./shared/pipe/convert-full-date.pipe";
import { ConvertForDatePipe } from "./shared/pipe/convert-for-date.pipe";
import { ActionPlanStatusPipe } from "./shared/pipe/action-plan-status.pipe";

/* Easy Components */
import { MessageComponent } from "./shared/component/message/message.component";
import { DashboardComponent } from "./dashboard/dashboard.component";
import { UserComponent } from "./user/user.component";
import { LoginComponent } from "./login/login.component";
import { LoadbarComponent } from "./shared/component/loadbar/loadbar.component";
import { UserTypeComponent } from "./user-type/user-type.component";
import { EmailComponent } from "./email/email.component";
import { InstituteComponent } from "./institute/institute.component";
import { InstituteTypeComponent } from "./institute-type/institute-type.component";
import { DepartamentComponent } from "./departament/departament.component";
import { DepartamentsModalComponent } from "./shared/component/departaments-modal/departaments-modal.component";
import { ChecklistComponent } from "./checklist/checklist.component";
import { ChecklistFormComponent } from "./checklist-form/checklist-form.component";
import { QuestionGroupComponent } from "./checklist-form/question-group/question-group.component";
import { QuestionGroupFormModalComponent } from "./checklist-form/question-group/question-group-form-modal/question-group-form-modal.component";
import { GMessageComponent } from "./shared/component/g-message/g-message.component";
import { UnconformityComponent } from "./unconformity/unconformity.component";
import { HeaderItemComponent } from "./checklist-form//header-item/header-item.component";
import { AttachmentListComponent } from "./attachment-list/attachment-list.component";
import { AttachmentModalComponent } from "./attachment-list/attachment-modal/attachment-modal.component";
import { InspectableModalComponent } from "./checklist-form/inspectable-modal/inspectable-modal.component";
import { HeaderItemModalComponent } from "./checklist-form/header-item/header-item-modal/header-item-modal.component";
import { ConfirmComponent } from "./shared/component/confirm/confirm.component";
import { QuestionFormModalComponent } from "./checklist-form/question-group/question/question-form-modal/question-form-modal.component";
import { UnconformityModalComponent } from "./checklist-form/question-group/question/question-form-modal/unconformity-modal/unconformity-modal.component";
import { QuestionComponent } from "./checklist-form/question-group/question/question.component";
import { ChecklistAnsweredComponent } from "./report/checklist-answered/checklist-answered.component";
import { QuestionUnconformityResponseComponent } from "./report/question-uncoformity-response/question-uncoformity-response.component";
import { ChecklistAnsweredDetailComponent } from "./report/checklist-answered/checklist-answered-detail/checklist-answered-detail.component";
import { NotificationModalComponent } from "./shared/component/notification-modal/notification-modal.component";
import { ApiModalComponent } from "./login/api-modal/api-modal.component";
import { ActionPlanComponent } from "./action-plan/action-plan.component";
import { ActionPlanModalComponent } from "./action-plan/action-plan-modal/action-plan-modal.component";

/* Easy Services */
import { BaseService } from "./shared/service/base.service";
import { ConfigSystemService } from "./shared/service/config-system.service";
import { UserService } from "./shared/service/user.service";
import { OrderService } from "./shared/service/order.service";
import { UserTypeService } from "./shared/service/user-type.service";
import { MenuService } from "./shared/service/menu.service";
import { EmailService } from "./shared/service/email.service";
import { InstituteService } from "./shared/service/institute.service";
import { InstituteTypeService } from "./shared/service/institute-type.service";
import { DepartamentService } from "./shared/service/departament.service";
import { InstituteDepartamentService } from "./shared/service/institute-departament.service";
import { ListComponent } from "./list/list.component";
import { ListValueModalComponent } from "./shared/component/list-value-modal/list-value-modal.component";
import { ListService } from "./shared/service/list.service";
import { ListValueService } from "./shared/service/list-value.service";
import { ChecklistService } from "./shared/service/checklist.service";
import { UserInstituteDepartamentService } from "./shared/service/user-institute-departament.service";
import { QuestionGroupService } from "./shared/service/question-group.service";
import { QuestionService } from "./shared/service/question.service";
import { UnconformityService } from "./shared/service/unconformity.service";
import { HeaderItemService } from "./shared/service/header-item.service";
import { AttachmentService } from "./shared/service/attachment.service";
import { ReportService } from "./shared/service/report.service";
import { SyncService } from "./shared/service/sync.service";
import { QuestionUnconformityResponseService } from "./shared/service/question-unconformity-response.service";
import { ActionPlanService } from "./shared/service/action-plan.service";
import { DynamicListService } from './shared/service/dynamic-list.service';
import { InspectionPlanService } from './shared/service/inspection-plan.service';

import { AuthGuard } from "./auth/auth.guard";
import { AuthService } from "./shared/service/auth.service";
import { HeaderDefaultComponent } from "./checklist-form/header-default/header-default.component";
import { HeaderDefaultModalComponent } from "./checklist-form/header-default/header-default-modal/header-default-modal.component";
import { HeaderDefaultService } from "./shared/service/header-default.service";
import { QrCodeWindowComponent } from "./qr-code-window/qr-code-window.component";
import { ConfigComponent } from "./config/config.component";
import { ConfigService } from "./shared/service/config.service";
import { FormatStringPipe } from "./shared/pipe/format-string.pipe";
import { EvaluetionFormModalComponent } from "./config/evaluetion-form-modal/evaluetion-form-modal.component";
import { ConvertConfigPipe } from "./shared/pipe/convert-config.pipe";
import { SchedulingComponent } from "./scheduling/scheduling.component";
import { SchedulingForm } from "./scheduling/scheduling-form/scheduling-form.component";
import { ProceedModalComponent } from "./shared/component/proceed-modal/proceed-modal.component";
import { SchedulingConfigService } from "./shared/service/scheduling-config.service";
import { SchedulingService } from "./shared/service/scheduling.service";
import { ActionPlanResponseService } from "./shared/service/action-plan-response.service";
import { ActionPlanResponseAttachmentService } from "./shared/service/action-plan-response-attachment.service";
import { QuestionAttachmentModalComponent } from "./checklist-form/question-group/question/question-attachment-modal/question-attachment-modal.component";
import { QuestionGroupAttachmentModalComponent } from "./checklist-form/question-group/question-group-attachment-modal/question-group-attachment-modal.component";
import { UnconformityItemComponent } from './shared/component/unconformity-item/unconformity-item.component';
import { FilterQueryComponent } from './shared/component/filter-query/filter-query.component';
import { ChecklistResponseQueryComponent } from './shared/component/checklist-response-filter-query/checklist-response-filter-query.component';
import { OrderFilterQueryComponent } from './shared/component/order-filter-query/order-filter-query.component';
import { ReviewModalComponent } from './shared/component/review-modal/review-modal.component';
import { DashboardService } from './shared/service/dashboard.service';
import { RedirectComponent } from './redirect/redirect.component';
import { ReviewComponent } from './review/review.component';
import { OrderComponent } from './order/order.component';
import { ReviewService } from './shared/service/review.service';
import { ReviewGroupService } from './shared/service/review-group.service';
import { OrderDetailComponent } from './order/order-detail/order-detail.component';
import { ReviewDetailComponent } from './review/review-detail/review-detail.component';

import { DynamicListComponent } from './dynamic-list/dynamic-list.component';
import { InspectionPlanComponent } from './inspection-plan/inspection-plan.component';
import { ChecklistEndpointService } from './shared/service/checklist-endpoint.service';
import { GenericIntegrationService } from './shared/service/generic-integration.service';
import { DynamicListItemComponent } from './dynamic-list-item/dynamic-list-item.component';
import { DynamicListFieldResponseService } from './shared/service/dynamic-list-response.service';
import { ChecklistEndpointFormComponent } from './checklist-form/endpoint-form/endpoint-form.component';
import { SchedulingQueryComponent } from './shared/component/scheduling-filter-query/scheduling-filter-query';
import { InspectionPlanFormComponent } from './checklist-form/inspection-plan-form/inspection-plan-form.component';
import { UserSignatureModalComponent } from './shared/component/user-signature-modal/user-signature-modal.component';
import { ListValueFilterModalComponent } from './shared/component/list-value-filter-modal/list-value-filter-modal.component';
import { QuestUncRespComponent } from './shared/component/question-unconformity-response/question-unconformity-response.component';
import { InstDynListItemModalComponent } from './shared/component/institute-dyn-list-item-modal/institute-dyn-list-item-modal.component';
import { InspectionPlanModalComponent } from './checklist-form/inspection-plan-form/inspection-plan-modal/inspection-plan-modal.component';
import { UserSignatureAttachmentModalComponent } from './user/user-signature-attachment-modal/user-signature-attachment-modal.component';
import { AiChatComponent } from './ai-chat/ai-chat.component';
import { AiChatMessageComponent } from './ai-chat/ai-chat-message/ai-chat-message.component';
import { AiChatMessageActionComponent } from './ai-chat/ai-chat-message/ai-chat-message-action/ai-chat-message-action.component';
import { AiThinkingComponent } from './ai-chat/components/ai-thinking/ai-thinking.component';
import { AiService } from './shared/service/ai.service';
import { AiChatService } from './ai-chat/services/ai-chat.service';
import { LocaleDatePipe } from './shared/pipe/locale-date.pipe';
import { MeasureTableComponent } from './measure-table/measure-table.component';
import { MeasureTableFormComponent } from './measure-table/measure-table-form/measure-table-form.component';
import { MeasureTableService } from './shared/service/measure-table.service';
import { ProductComponent } from './product/product.component';
import { ProductFormComponent } from './product/product-form/product-form.component';
import { ProductService } from './shared/service/product.service';

export function HttpLoaderFactory(http: Http) {
  return new TranslateHttpLoader(http);
}

@NgModule({
  imports: [
    BrowserModule,
    FormsModule,
    AppRoutes,
    HttpModule,
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [Http],
      },
    }),
    BrowserModule,
    FormsModule,
    AppRoutes,
    ProgressSpinnerModule,
    BrowserAnimationsModule,
    AccordionModule,
    AutoCompleteModule,
    BreadcrumbModule,
    ButtonModule,
    CalendarModule,
    CarouselModule,
    ColorPickerModule,
    ChartModule,
    CheckboxModule,
    ChipsModule,
    CodeHighlighterModule,
    ConfirmDialogModule,
    SharedModule,
    ContextMenuModule,
    DataGridModule,
    DataListModule,
    DataScrollerModule,
    DataTableModule,
    DialogModule,
    DragDropModule,
    DropdownModule,
    EditorModule,
    FieldsetModule,
    FileUploadModule,
    GalleriaModule,
    GMapModule,
    GrowlModule,
    InputMaskModule,
    InputSwitchModule,
    InputTextModule,
    InputTextareaModule,
    LightboxModule,
    ListboxModule,
    MegaMenuModule,
    MenuModule,
    MenubarModule,
    MessageModule,
    MessagesModule,
    MultiSelectModule,
    OrderListModule,
    OrganizationChartModule,
    OverlayPanelModule,
    PaginatorModule,
    PanelModule,
    PanelMenuModule,
    PasswordModule,
    PickListModule,
    ProgressBarModule,
    RadioButtonModule,
    RatingModule,
    ScheduleModule,
    SelectButtonModule,
    SlideMenuModule,
    SliderModule,
    SpinnerModule,
    SplitButtonModule,
    StepsModule,
    TabMenuModule,
    TabViewModule,
    TerminalModule,
    TieredMenuModule,
    ToggleButtonModule,
    ToolbarModule,
    TooltipModule,
    TreeModule,
    TreeTableModule,
    TriStateCheckboxModule,
  ],
  declarations: [
    MessageComponent,
    AppComponent,
    AppMenuComponent,
    AppSubMenu,
    AppTopBar,
    AppFooter,
    AppRightPanel,
    InlineProfileComponent,
    DashboardComponent,
    ReviewComponent,
    OrderComponent,
    OrderDetailComponent,
    ReviewDetailComponent,
    GetWeekDayPipe,
    GetBooleanPipe,
    ConvertDatePipe,
    LocaleDatePipe,
    UserComponent,
    LoginComponent,
    LoadbarComponent,
    ConvertSecondsToDatePipe,
    UserTypeComponent,
    ReplacePipe,
    DecimalPipe,
    ConvertFullDatePipe,
    EmailComponent,
    InstituteComponent,
    InstituteComponent,
    InstituteTypeComponent,
    DepartamentComponent,
    DepartamentsModalComponent,
    ListComponent,
    DynamicListComponent,
    DynamicListItemComponent,
    ListValueModalComponent,
    ListValueFilterModalComponent,
    UserSignatureModalComponent,
    ReviewModalComponent,
    ChecklistComponent,
    ChecklistFormComponent,
    QuestionGroupComponent,
    QuestionGroupFormModalComponent,
    QuestionFormModalComponent,
    InspectionPlanComponent,
    InspectionPlanFormComponent,
    InspectionPlanModalComponent,
    ChecklistEndpointFormComponent,
    GMessageComponent,
    UnconformityComponent,
    UnconformityModalComponent,
    HeaderItemComponent,
    AttachmentListComponent,
    AttachmentModalComponent,
    InspectableModalComponent,
    HeaderItemModalComponent,
    ConfirmComponent,
    QuestionComponent,
    ChecklistAnsweredComponent,
    QuestionUnconformityResponseComponent,
    QuestUncRespComponent, // report
    ChecklistAnsweredDetailComponent,
    NotificationModalComponent,
    InstDynListItemModalComponent,
    ApiModalComponent,
    HeaderDefaultComponent,
    HeaderDefaultModalComponent,
    QrCodeWindowComponent,
    ActionPlanComponent,
    ConvertForDatePipe,
    ActionPlanModalComponent,
    ActionPlanStatusPipe,
    ConfigComponent,
    FormatStringPipe,
    EvaluetionFormModalComponent,
    ConvertConfigPipe,
    SchedulingComponent,
    SchedulingForm,
    ProceedModalComponent,
    QuestionAttachmentModalComponent,
    QuestionGroupAttachmentModalComponent,
    UnconformityItemComponent,
    FilterQueryComponent,
    OrderFilterQueryComponent,
    ChecklistResponseQueryComponent,
    SchedulingQueryComponent,
    RedirectComponent,
    AiChatComponent,
    AiChatMessageComponent,
    AiChatMessageActionComponent,
    AiThinkingComponent,
    MeasureTableComponent,
    MeasureTableFormComponent,
    ProductComponent,
    ProductFormComponent,
    // UserSignatureAttachmentModalComponent
  ],
  providers: [
    BaseService,
    ConfirmationService,
    ConfigSystemService,
    UserService,
    OrderService,
    UserTypeService,
    ReviewService,
    ReviewGroupService,
    AuthGuard,
    AuthService,
    MenuService,
    EmailService,
    InstituteService,
    InstituteTypeService,
    DashboardService,
    DepartamentService,
    InstituteDepartamentService,
    ListService,
    ListValueService,
    DynamicListService,
    InspectionPlanService,
    ChecklistEndpointService,
    DynamicListFieldResponseService,
    ChecklistService,
    UserInstituteDepartamentService,
    QuestionGroupService,
    QuestionService,
    UnconformityService,
    HeaderItemService,
    AttachmentService,
    ReportService,
    SyncService,
    QuestionUnconformityResponseService,
    GenericIntegrationService,
    HeaderDefaultService,
    ActionPlanService,
    ActionPlanResponseService,
    ActionPlanResponseAttachmentService,
    ConvertDatePipe,
    ConfigService,
    FormatStringPipe,
    SchedulingConfigService,
    SchedulingService,
    AiService,
    AiChatService,
    MeasureTableService,
    ProductService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}



// WEBPACK FOOTER //
// C:/Users/Emanuel/Documents/EasyCheck/easy-check-front-web/src/app/app.module.ts